import { Injectable } from '@angular/core';

import {
  DocumentPendingStaffApproval,
  InboxCounts,
  InvalidDocument,
  MembershipPendingStaffApprovalInboxItem,
  StaffApprovalAction,
  UnlinkedEntryPassCustomerInboxItem,
  UnlinkedMembershipCustomerInboxItem,
  UnlinkedVoucherPackCustomerInboxItem,
  UnlinkedReservation, PotentialDuplicateCustomer, PotentialDuplicateCustomerFilters
} from '../../models/inbox';
import { ApiService } from '../api/api.service';
import { HttpParams } from '@angular/common/http';
import { ListResponse } from '../../models/list-response';

@Injectable({
  providedIn: 'root'
})
export class InboxService {
  /**
   * A service providing access to inbox-related functions.
   */

  // A local copy is kept in memory so that components do not need to load the
  // counts each time they use them.
  private inboxCounts: InboxCounts = null;

  // The server error code experienced when trying to load inbox counts, if
  // any. This is not directly available to components; it must be obtained via
  // getInboxCountsServerError.
  private inboxCountsServerError: number = null;

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Load the user's inbox counts from the API.
   *
   * @return A promise which resolves when the inbox counts are loaded (and
   *   therefore are available via getInboxCounts).
   */
  loadInboxCounts(refreshCache: boolean): Promise<void> {
    this.inboxCountsServerError = null;

    let params = new HttpParams();

    if (refreshCache) {
      params = params.set('refresh_cache', 'true');
    }

    return this.apiService.getFromAPI<InboxCounts>(
      'gym-endpoints/inbox-counts/',
      params,
      false,
      true
    )
      .then(inboxCounts => {
        this.inboxCounts = inboxCounts;
      })
      .catch(reason => {
        this.inboxCountsServerError = reason.status;
      });
  }

  /**
   * Return a deep copy of the user's inbox counts. A deep copy is used to
   * prevent components from editing the counts and thereby impacting other
   * components.
   *
   * @return The inbox counts object.
   */
  getInboxCounts(): InboxCounts {
    if (this.inboxCounts === null) {
      return this.inboxCounts;
    }

    const inboxCountsCopy = {};

    // Note: this only works a long as InboxCounts has no nested objects.
    for (const key of Object.keys(this.inboxCounts)) {
      inboxCountsCopy[key] = this.inboxCounts[key];
    }

    return inboxCountsCopy as InboxCounts;
  }

  getInboxCountsServerError(): number {
    return this.inboxCountsServerError;
  }

  getUnlinkedReservations(): Promise<UnlinkedReservation[]> {
    return this.apiService.getFromAPI<UnlinkedReservation[]>(
      'gym-endpoints/unlinked-reservations/'
    );
  }

  getUnlinkedReservation(unlinkedReservationId: number): Promise<UnlinkedReservation> {
    return this.apiService.getFromAPI<UnlinkedReservation>(
      'gym-endpoints/unlinked-reservations/' + unlinkedReservationId.toString() + '/'
    );
  }

  linkUnlinkedReservation(unlinkedReservationId: number, customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/link-unlinked-reservation/' + unlinkedReservationId.toString() + '/' + customerId.toString() + '/',
      {}
    ).then();
  }

  createNewCustomerForUnlinkedReservation(unlinkedReservationId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/create-new-customer-for-unlinked-reservation/' + unlinkedReservationId.toString() + '/',
      {}
    ).then();
  }

  hideUnlinkedReservation(unlinkedReservationId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/hide-unlinked-reservation/' + unlinkedReservationId.toString() + '/',
      {}
    ).then();
  }

  getActiveInvalidDocuments(): Promise<InvalidDocument[]> {
    return this.apiService.getFromAPI<InvalidDocument[]>(
      'gym-endpoints/inbox/invalid-documents/active/'
    );
  }

  getActiveInvalidDocumentsForCustomer(customerId: number): Promise<InvalidDocument[]> {
    return this.apiService.getFromAPI<InvalidDocument[]>(
      'gym-endpoints/inbox/invalid-documents/active-for-customer/' + customerId.toString() + '/'
    );
  }

  getRecentlyDismissedInvalidDocuments(): Promise<InvalidDocument[]> {
    return this.apiService.getFromAPI<InvalidDocument[]>(
      'gym-endpoints/inbox/invalid-documents/recently-dismissed/'
    );
  }

  dismissInvalidDocumentInboxItem(inboxItemId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/invalid-documents/' + inboxItemId.toString() + '/dismiss/',
      {}
    ).then();
  }

  getActiveDocumentsPendingStaffApproval(pageNum: number): Promise<DocumentPendingStaffApproval[]> {
    let params = new HttpParams();
    params = params.append('page_num', pageNum.toString());

    return this.apiService.getFromAPI<DocumentPendingStaffApproval[]>(
      'gym-endpoints/inbox/documents-pending-staff-approval/active/',
      params
    );
  }

  getRecentlyDismissedDocumentsPendingStaffApproval(): Promise<DocumentPendingStaffApproval[]> {
    return this.apiService.getFromAPI<DocumentPendingStaffApproval[]>(
      'gym-endpoints/inbox/documents-pending-staff-approval/recently-dismissed/'
    );
  }

  getDocumentPendingStaffApprovalInboxItem(documentId: string): Promise<DocumentPendingStaffApproval> {
    return this.apiService.getFromAPI<DocumentPendingStaffApproval>(
      'gym-endpoints/inbox/documents-pending-staff-approval/get/' + documentId.toString() + '/'
    );
  }

  manageDocumentPendingStaffApproval(documentId: string, action: StaffApprovalAction): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/documents-pending-staff-approval/manage/' + documentId.toString() + '/',
      {action}
    ).then();
  }

  getUnlinkedMembershipCustomers(): Promise<UnlinkedMembershipCustomerInboxItem[]> {
    return this.apiService.getFromAPI<UnlinkedMembershipCustomerInboxItem[]>(
      'gym-endpoints/inbox/unlinked-membership-customers/active/'
    );
  }

  getUnlinkedMembershipCustomer(unlinkedMembershipCustomerId: number): Promise<UnlinkedMembershipCustomerInboxItem> {
    return this.apiService.getFromAPI<UnlinkedMembershipCustomerInboxItem>(
      'gym-endpoints/inbox/unlinked-membership-customers/get/' + unlinkedMembershipCustomerId.toString() + '/'
    );
  }

  linkUnlinkedMembershipCustomer(unlinkedMembershipCustomerId: number, customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/unlinked-membership-customers/link/' + unlinkedMembershipCustomerId.toString()
      + '/' + customerId.toString() + '/',
      {}
    ).then();
  }

  createNewCustomerForUnlinkedMembershipCustomer(unlinkedMembershipCustomerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/unlinked-membership-customers/create-new/' + unlinkedMembershipCustomerId.toString() + '/',
      {}
    ).then();
  }

  getUnlinkedEntryPassCustomers(): Promise<UnlinkedEntryPassCustomerInboxItem[]> {
    return this.apiService.getFromAPI<UnlinkedEntryPassCustomerInboxItem[]>(
      'gym-endpoints/inbox/unlinked-entry-pass-customers/active/'
    );
  }

  getUnlinkedEntryPassCustomer(unlinkedEntryPassCustomerId: number): Promise<UnlinkedEntryPassCustomerInboxItem> {
    return this.apiService.getFromAPI<UnlinkedEntryPassCustomerInboxItem>(
      'gym-endpoints/inbox/unlinked-entry-pass-customers/get/' + unlinkedEntryPassCustomerId.toString() + '/'
    );
  }

  linkUnlinkedEntryPassCustomer(unlinkedEntryPassCustomerId: number, customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/unlinked-entry-pass-customers/link/' + unlinkedEntryPassCustomerId.toString()
      + '/' + customerId.toString() + '/',
      {}
    ).then();
  }

  createNewCustomerForUnlinkedEntryPassCustomer(unlinkedEntryPassCustomerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/unlinked-entry-pass-customers/create-new/' + unlinkedEntryPassCustomerId.toString() + '/',
      {}
    ).then();
  }

  getUnlinkedVoucherPackCustomers(): Promise<UnlinkedVoucherPackCustomerInboxItem[]> {
    return this.apiService.getFromAPI<UnlinkedVoucherPackCustomerInboxItem[]>(
      'gym-endpoints/inbox/unlinked-voucher-pack-customers/active/'
    );
  }

  getUnlinkedVoucherPackCustomer(unlinkedVoucherPackCustomerId: number): Promise<UnlinkedVoucherPackCustomerInboxItem> {
    return this.apiService.getFromAPI<UnlinkedVoucherPackCustomerInboxItem>(
      'gym-endpoints/inbox/unlinked-voucher-pack-customers/get/' + unlinkedVoucherPackCustomerId.toString() + '/'
    );
  }

  linkUnlinkedVoucherPackCustomer(unlinkedVoucherPackCustomerId: number, customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/unlinked-voucher-pack-customers/link/' + unlinkedVoucherPackCustomerId.toString()
      + '/' + customerId.toString() + '/',
      {}
    ).then();
  }

  createNewCustomerForUnlinkedVoucherPackCustomer(unlinkedVoucherPackCustomerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/unlinked-voucher-pack-customers/create-new/' + unlinkedVoucherPackCustomerId.toString() + '/',
      {}
    ).then();
  }

  getMembershipsPendingStaffApproval(): Promise<MembershipPendingStaffApprovalInboxItem[]> {
    return this.apiService.getFromAPI<MembershipPendingStaffApprovalInboxItem[]>(
      'gym-endpoints/inbox/memberships-pending-approval/active/'
    );
  }

  approveMembershipPendingStaffApproval(customerMembershipId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/memberships-pending-approval/manage/' + customerMembershipId.toString() + '/',
      {action: 'approve'}
    ).then();
  }

  cancelMembershipPendingStaffApproval(customerMembershipId: number, emailText: string): Promise<{has_pos_payment: boolean}> {
    return this.apiService.postToAPI<{has_pos_payment: boolean}>(
      'gym-endpoints/inbox/memberships-pending-approval/manage/' + customerMembershipId.toString() + '/',
      {
        action: 'cancel',
        email_text: emailText
      }
    );
  }

  hideMembershipPendingStaffApproval(customerMembershipId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/inbox/memberships-pending-approval/manage/' + customerMembershipId.toString() + '/',
      {action: 'hide'}
    ).then();
  }

  getPotentialDuplicateCustomers(pageNumber: number,
                                 pageSize: number,
                                 filters: PotentialDuplicateCustomerFilters,
                                 isOperational: boolean): Promise<ListResponse<PotentialDuplicateCustomer>> {
    return this.apiService.getCollection<PotentialDuplicateCustomer, PotentialDuplicateCustomerFilters>(
      'potential-duplicate-customers/',
      pageNumber,
      pageSize,
      filters,
      isOperational
    );
  }

  getPotentialDuplicateCustomer(id: number): Promise<PotentialDuplicateCustomer> {
    return this.apiService.getItem<PotentialDuplicateCustomer>(
      'potential-duplicate-customers/',
      id
    );
  }

  mergePotentialDuplicateCustomer(id: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'potential-duplicate-customers/' + id.toString() + '/manage/',
      {
        action: 'merge'
      }
    ).then();
  }

  dismissPotentialDuplicateCustomer(id: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'potential-duplicate-customers/' + id.toString() + '/manage/',
      {
        action: 'dismiss'
      }
    ).then();
  }

  hidePotentialDuplicateCustomer(id: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'potential-duplicate-customers/' + id.toString() + '/manage/',
      {
        action: 'hide'
      }
    ).then();
  }
}
